// client/src/pages/School_Admin/Academics/TimetableGenerateTab.js
//
// GENERATE · REVIEW · APPLY.
//
// Generating writes nothing. It produces a proposal, held on the server, which
// this screen shows and which Apply then writes in one transaction — see
// migration 20260910090000 for why a generated week never goes straight into
// `timetables`.
//
// EVERY FAILURE IS NAMED. A solver that says "could not generate" tells a school
// nothing it can act on; this shows which lesson, in which class, and what stood
// in the way — and separately, what could not even be attempted because it has
// no teacher.

import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, AlertTitle, Box, Button, Card, CardContent, Chip, CircularProgress,
  Collapse, Divider, IconButton, LinearProgress, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HistoryIcon from '@mui/icons-material/History';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import PublishIcon from '@mui/icons-material/Publish';
import { fetchJson } from '../../../utils/fetchJson';
import MasterGrid from './TimetableMasterGrid';

const API = '/api/academics/timetables';

/** "3 doubles + 1 single" - how a subject's periods break up under its settings. */
function runsAs(lengths) {
  const word = (n) => ({ 1: 'single', 2: 'double', 3: 'triple', 4: 'quadruple' }[n] || `run of ${n}`);
  const counts = new Map();
  for (const n of lengths) counts.set(n, (counts.get(n) || 0) + 1);
  return [...counts.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([n, c]) => `${c} ${word(n)}${c === 1 ? '' : 's'}`)
    .join(' + ');
}

/**
 * WHAT THE SETTINGS WILL PRODUCE, BEFORE ANYTHING IS SEARCHED FOR.
 *
 * A school sets "6 periods, up to 2 in a row, twice a day, side by side" and has
 * no way of knowing that means three doubles rather than six singles - and no
 * way of knowing, until it has read a wall of failures, that one teacher has
 * been handed 34 periods against a ceiling of 24. Both are arithmetic, both take
 * milliseconds, and both are far more use before the search than after it.
 */
function Forecast({ data, loading }) {
  const [open, setOpen] = useState(false);

  if (loading) return <LinearProgress />;
  if (!data) return null;

  const over = (data.teachers || []).filter((t) => {
    const cap = Number(t.caps?.maxPeriodsPerWeek);
    return Number.isFinite(cap) && cap > 0 && t.periods > cap;
  });
  const fullClasses = (data.classes || []).filter((c) => c.free <= 0);

  return (
    <Card variant="outlined">
      <CardContent sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            What these settings will produce
          </Typography>
          <Chip size="small" label={`${data.totals.periods} periods`} />
          <Chip size="small" variant="outlined" label={`${data.totals.units} things to place`} />
          <Chip size="small" variant="outlined" label={`${data.totals.seats} seats in the week`} />
          {!!over.length && (
            <Chip size="small" color="error" label={`${over.length} over their weekly cap`} />
          )}
          {!!fullClasses.length && (
            <Chip size="small" color="warning" variant="outlined"
              label={`${fullClasses.length} class${fullClasses.length === 1 ? '' : 'es'} with no spare period`} />
          )}
          {!!(data.unstaffed || []).length && (
            <Chip size="small" color="warning" variant="outlined"
              label={`${data.unstaffed.length} with no teacher`} />
          )}
          <Box sx={{ flex: 1 }} />
          <IconButton size="small" onClick={() => setOpen((v) => !v)}>
            {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          A class with no spare period can only be filled if every other rule happens to agree;
          one teacher over their cap is a week that cannot be built at all, whatever the search does.
        </Typography>

        <Collapse in={open} unmountOnExit>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Per teacher</Typography>
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Teacher</TableCell>
                  <TableCell align="right">Periods</TableCell>
                  <TableCell align="right">Weekly cap</TableCell>
                  <TableCell align="right">Classes</TableCell>
                  <TableCell align="right">Subjects</TableCell>
                  <TableCell>Days declared</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.teachers || []).map((t) => {
                  const cap = Number(t.caps?.maxPeriodsPerWeek);
                  const capped = Number.isFinite(cap) && cap > 0;
                  const isOver = capped && t.periods > cap;
                  return (
                    <TableRow key={t.key} hover>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2">{t.name}</Typography>
                          {t.noAccount && (
                            <Chip size="small" variant="outlined" color="warning" sx={{ height: 18 }}
                              label="no account" />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color={isOver ? 'error' : 'text.primary'}
                          sx={{ fontWeight: isOver ? 700 : 400 }}>
                          {t.periods}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{capped ? cap : '\u2014'}</TableCell>
                      <TableCell align="right">{t.classes}</TableCell>
                      <TableCell align="right">{t.subjects}</TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {t.declaredMoments == null
                            ? 'not declared \u2014 every period assumed open'
                            : `${t.declaredMoments} periods on site`}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Per subject</Typography>
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Class</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell align="right">Periods</TableCell>
                  <TableCell>Placed as</TableCell>
                  <TableCell>Taught by</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.subjects || []).map((r) => (
                  <TableRow key={`${r.classId}|${r.subjectId}`} hover>
                    <TableCell><Typography variant="caption">{r.className}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{r.subjectName}</Typography></TableCell>
                    <TableCell align="right">{r.periods}</TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{runsAs(r.lengths)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption"
                        color={r.teachers.some((x) => !x.key) ? 'warning.main' : 'text.secondary'}>
                        {r.teachers.map((x) => `${x.name} (${x.periods})`).join(', ')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Collapse>
      </CardContent>
    </Card>
  );
}

export default function GenerateTab({ teachers, onApplied }) {
  const { t } = useTranslation();
  const [seed, setSeed] = useState(1);
  const [running, setRunning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [partial, setPartial] = useState(null);
  // Why the arithmetic refused, when it did. The server names every fault; the
  // tab used to throw them away and show the one-line summary.
  const [blocked, setBlocked] = useState(null);
  const [applied, setApplied] = useState(null);
  const [runs, setRuns] = useState([]);
  // WHAT THE TIMETABLE PAGE WILL CALL IT. That page lists timetables by name and
  // shows nothing until one is chosen, so a week applied without a name is a
  // week nobody can open.
  const [timetableName, setTimetableName] = useState('Generated timetable');
  const [forecast, setForecast] = useState(null);
  const [loadingForecast, setLoadingForecast] = useState(true);

  const loadForecast = useCallback(async () => {
    setLoadingForecast(true);
    try { setForecast(await fetchJson(`${API}/solver/forecast`)); } catch (_) { setForecast(null); } finally { setLoadingForecast(false); }
  }, []);

  const loadRuns = useCallback(async () => {
    try { setRuns((await fetchJson(`${API}/solver/runs`))?.runs || []); } catch (_) { /* the list is a convenience */ }
  }, []);

  useEffect(() => { loadRuns(); loadForecast(); }, [loadRuns, loadForecast]);

  /**
   * A SAVED WEEK, REOPENED — nothing is generated.
   *
   * Every run has always been stored; nothing could read one back, so a school
   * that found an arrangement it liked and navigated away lost it and had to
   * spend another twenty seconds hoping for the same one. It comes back in the
   * same shape a fresh run has, so the chart, the PDFs and Apply all behave
   * exactly as they would on one.
   */
  const open = async (id) => {
    setRunning(true);
    setError('');
    setPartial(null);
    setBlocked(null);
    setApplied(null);
    try {
      const body = await fetchJson(`${API}/solver/run/${id}`);
      setResult(body);
      setSeed(body.seed || 1);
    } catch (e) {
      setError(e?.message || 'Could not open that week.');
    } finally {
      setRunning(false);
    }
  };

  const discard = async (id) => {
    if (!window.confirm('Discard this saved week? The setup it was built from is untouched.')) return;
    try {
      await fetchJson(`${API}/solver/run/${id}`, { method: 'DELETE' });
      if (result?.runId === id) setResult(null);
      loadRuns();
    } catch (e) {
      setError(e?.message || 'Could not discard it.');
    }
  };

  const run = async (withSeed) => {
    setRunning(true);
    setError('');
    setPartial(null);
    setBlocked(null);
    setApplied(null);
    try {
      const body = await fetchJson(`${API}/solver/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ seed: withSeed }),
      });
      setSeed(withSeed);
      setResult(body);
      loadRuns();
    } catch (e) {
      // THE FINDINGS, NOT THE SUMMARY. The pre-check exists to name every fault
      // and say which number to change; showing only "this week cannot exist"
      // tells a school nothing it can act on, and the reasons were sitting on
      // the error the whole time.
      const body = e?.data;
      if (body?.blocked && Array.isArray(body.findings) && body.findings.length) {
        setBlocked({ message: body.message, findings: body.findings });
      } else {
        setError(e?.message || 'Could not generate.');
      }
      setResult(null);
    } finally {
      setRunning(false);
    }
  };

  const apply = async (acceptPartial) => {
    setApplying(true);
    setError('');
    try {
      const body = await fetchJson(`${API}/solver/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          runId: result.runId,
          acceptPartial: !!acceptPartial,
          timetableName: timetableName.trim() || undefined,
        }),
      });
      setApplied(body);
      setPartial(null);
      loadRuns();
      if (onApplied) onApplied();
    } catch (e) {
      // A partial run is refused once, with the consequence stated, and only
      // then offered. The refusal is not an error to be retried blindly.
      if (/could not be placed/.test(e?.message || '')) setPartial(e.message);
      else setError(e?.message || 'Could not apply.');
    } finally {
      setApplying(false);
    }
  };

  const unplaced = result?.unplaced || [];
  const unplaceable = result?.unplaceable || [];
  const suggestions = result?.suggestions || [];
  // PERIODS, NOT UNITS. A double is one thing to place and two periods in the
  // week, and a school counts the week in periods — "12 could not be placed"
  // meant 12 units and up to 24 periods, which is not what anybody read it as.
  const placedPeriods = (result?.placements || []).reduce((n, p) => n + (p.slots?.length || 1), 0);
  const short = unplaced.reduce((n, u) => n + (u.length || 1), 0);

  return (
    <Stack spacing={2}>
      <Card variant="outlined" className="no-print">
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button
              variant="contained" startIcon={<AutoFixHighIcon />}
              disabled={running || applying} onClick={() => run(seed)}
            >
              {running ? 'Generating…' : 'Generate the week'}
            </Button>
            <Button
              startIcon={<ShuffleIcon />} disabled={running || applying || !result}
              onClick={() => run(seed + 1)}
            >
              Try another arrangement
            </Button>
            <TextField
              size="small" label="Name it" sx={{ width: 200 }}
              helperText="How it is listed on the timetable page"
              value={timetableName}
              onChange={(e) => setTimetableName(e.target.value)}
            />
            <TextField
              size="small" label="Seed" type="number" sx={{ width: 100 }}
              inputProps={{ min: 1 }}
              value={seed} onChange={(e) => setSeed(Math.max(1, Number(e.target.value) || 1))}
            />
            <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 420 }}>
              The same inputs and the same seed always give the same week, so a chart you have
              printed can be reproduced exactly. "Try another arrangement" is the next seed.
            </Typography>
          </Stack>
          {running && <LinearProgress sx={{ mt: 2 }} />}
        </CardContent>
      </Card>

      <Forecast data={forecast} loading={loadingForecast} />

      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

      {blocked && (
        <Alert severity="error" onClose={() => setBlocked(null)}>
          <AlertTitle>
            {t('timetableGenerator.generate.blockedTitle', {
              count: blocked.findings.length,
              defaultValue: 'This week cannot exist as described — {{count}} things to fix',
            })}
          </AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('timetableGenerator.generate.blockedBody', {
              defaultValue: 'Nothing was searched for, because counting already shows it is '
                + 'impossible. Each line below names what to change.',
            })}
          </Typography>
          <Stack spacing={0.5}>
            {blocked.findings.slice(0, 15).map((f, i) => (
              <Typography key={i} variant="body2">• {f.message}</Typography>
            ))}
            {blocked.findings.length > 15 && (
              <Typography variant="caption">
                {t('timetableGenerator.generate.blockedMore', {
                  count: blocked.findings.length - 15,
                  defaultValue: '…and {{count}} more. Readiness lists them all.',
                })}
              </Typography>
            )}
          </Stack>
        </Alert>
      )}

      {/* NOT "this week". The rows are written against the academic year and
          repeat every week of it until somebody changes them — a screen that
          says otherwise sends a school looking for the control that sets the
          rest of the year. */}
      {applied && (
        <Alert severity="success">
          <AlertTitle>{applied.replaced ? 'Replaced' : 'In use'}</AlertTitle>
          {applied.periods ?? applied.lessons} periods across {applied.classes} classes, for the
          whole academic year.{applied.replaced ? ' The timetable that was in use has been replaced.' : ''}
          {' '}Registers, messaging and every teacher&apos;s class list now read from it. It can be
          edited class by class on the timetable page, or replaced here by using another saved week.
        </Alert>
      )}

      {partial && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" disabled={applying} onClick={() => apply(true)}>
              Apply anyway
            </Button>
          }
        >
          <AlertTitle>This week is incomplete</AlertTitle>
          {partial}
        </Alert>
      )}

      {result && (
        <>
          {!!(result.faults || []).length && (
            <Alert severity="error">
              <AlertTitle>
                This week has {result.faults.length} period
                {result.faults.length === 1 ? '' : 's'} with two subjects in it
              </AlertTitle>
              It was generated before that fault was fixed, so it cannot be used and cannot be
              repaired. Generate again. What you are seeing in those cells is not a pairing —
              a pairing only ever comes from the Pairing tab.
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {result.faults.slice(0, 5).map((m, i) => (
                  <Typography key={i} variant="body2">• {m}</Typography>
                ))}
              </Stack>
            </Alert>
          )}

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap className="no-print">
            {result.loaded && (
              <Chip size="small" color="info" variant="outlined"
                label={`saved week · ${new Date(result.savedAt).toLocaleString()}`} />
            )}
            <Chip
              color={result.status === 'ready' ? 'success' : (result.status === 'partial' ? 'warning' : 'error')}
              label={result.status === 'ready' ? 'Every period placed'
                : (result.status === 'partial'
                  ? `${short} of ${short + placedPeriods} periods could not be placed`
                  : 'Nothing could be placed')}
            />
            <Chip size="small" variant="outlined" label={`${placedPeriods} periods placed`} />
            {!!result.rescued && (
              <Chip size="small" color="error"
                label={`${result.rescued} removed by the safety sweep — please report this`} />
            )}
            {!!result.repaired && (
              <Chip size="small" variant="outlined"
                label={`${result.repaired} placed by making room`} />
            )}
            <Chip size="small" variant="outlined" label={`seed ${result.seed}`} />
            <Chip size="small" variant="outlined" label={`${result.steps} steps · ${result.ms} ms`} />
            {result.score && (
              <Chip size="small" variant="outlined"
                label={`${result.score.daysOnSite} teacher-days · ${result.score.gaps} gaps`} />
            )}
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained" color="primary" startIcon={<PublishIcon />}
              disabled={applying || !result.placements.length || !!applied
                || !!(result.faults || []).length}
              onClick={() => apply(false)}
            >
              {applying ? 'Putting it in use…' : 'Use this for the year'}
            </Button>
          </Stack>

          {result.exhausted && (
            <Alert severity="info">
              The search hit its budget before exploring everything. What is below is valid; a
              different seed may place more.
            </Alert>
          )}

          {/* WHAT TO CHANGE, ABOVE WHAT WENT WRONG. A list of failures is a
              list of failures; the line under each one names the setting that
              is standing in the way and the person it belongs to. */}
          {!!suggestions.length && (
            <Alert severity="warning">
              <AlertTitle>
                {short} period{short === 1 ? '' : 's'} could not be placed — here is what would let them in
              </AlertTitle>
              <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                {suggestions.map((g, i) => (
                  <Typography key={i} variant="body2">• {g.message}</Typography>
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Each line is the first change that opens a slot, cheapest first. Change one and
                generate again — the rest of the week stays as it is for the same seed.
              </Typography>
            </Alert>
          )}

          {!!unplaceable.length && (
            <Alert severity="error">
              <AlertTitle>Not even attempted</AlertTitle>
              {unplaceable.map((u, i) => (
                <Typography key={i} variant="body2">
                  • {u.subjectName} ({u.periods} periods) — {u.reason}.
                </Typography>
              ))}
            </Alert>
          )}

          <Divider className="no-print" />
          <MasterGrid result={result} teachers={teachers} />
        </>
      )}

      {!result && !running && (
        <Alert severity="info">
          Nothing generated yet. Everything the solver needs is recorded on the Setup tabs — the
          school day, each subject&apos;s periods, who teaches which class, and when they are on
          site. Readiness will tell you whether the week you have described can exist before you
          spend time on this.
        </Alert>
      )}

{/* EVERY WEEK THAT WAS EVER GENERATED, AND OPENABLE.
          Generating takes twenty seconds and a different seed gives a different
          arrangement, so a school that liked one and moved on had no way back to
          it. These were stored the whole time and nothing could read them. */}
      {!!runs.length && (
        <Card variant="outlined">
          <CardContent sx={{ pb: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <HistoryIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Saved weeks</Typography>
              <Typography variant="caption" color="text.secondary">
                Open one to look at it, print it or apply it — without generating again.
              </Typography>
            </Stack>
            <Stack spacing={0.5}>
              {runs.map((r) => {
                const showing = result?.runId === r.id;
                return (
                  <Stack key={r.id} direction="row" spacing={1} alignItems="center"
                    flexWrap="wrap" useFlexGap
                    sx={{ py: 0.5, px: 1, borderRadius: 1, bgcolor: showing ? 'action.selected' : undefined }}
                  >
                    <Chip
                      size="small"
                      variant={r.appliedAt ? 'filled' : 'outlined'}
                      color={r.appliedAt ? 'success' : 'default'}
                      label={r.appliedAt ? 'in use' : `seed ${r.seed}`}
                    />
                    <Typography variant="body2">{r.periods ?? 0} periods</Typography>
                    {!!r.unplaced && (
                      <Typography variant="caption" color="warning.main">
                        {r.unplaced} short
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {r.classes} classes · {new Date(r.createdAt).toLocaleString()}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    {showing && <Chip size="small" color="primary" label="showing" />}
                    {!!r.faults && (
                      <Chip size="small" color="error" variant="outlined"
                        label={`${r.faults} period${r.faults === 1 ? '' : 's'} double-booked`} />
                    )}
                    <Button
                      size="small" disabled={running || applying || !r.loadable || showing}
                      onClick={() => open(r.id)}
                    >
                      {r.loadable ? 'Open' : 'Not openable'}
                    </Button>
                    <IconButton
                      size="small" disabled={running || applying || !!r.appliedAt}
                      onClick={() => discard(r.id)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                );
              })}
            </Stack>
            {runs.some((r) => !r.loadable) && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                A week saved before this feature existed has its periods but not the day it was
                built on, so it cannot be drawn. Generate again to replace it.
              </Typography>
            )}
            {/* WHY A SAVED WEEK SHOWS TWO SUBJECTS IN ONE PERIOD. It is not a
                pairing — it is a fault, in weeks built while the repair pass
                could leave two lessons in one slot. Nothing can repair them;
                saying so here is the difference between regenerating and
                hunting for a pairing that was never declared. */}
            {runs.some((r) => !!r.faults) && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                Some saved weeks have two subjects in one period. They were generated before
                that fault was fixed, they cannot be applied, and they cannot be repaired —
                generate again to replace them. Two subjects in one period is otherwise only ever
                a pairing you declared on the Pairing tab, and there are none declared.
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {(running || applying) && !result && <CircularProgress size={20} />}
    </Stack>
  );
}
